import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {Box, CircularProgress, Tab, Tabs, Typography} from '@mui/material'
import EmptyState from 'src/components-new/layout/EmptyState'
import Filters, {FilterItem, FilterResult} from 'src/components-new/data/Filters'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import {STATUS} from 'src/constants/constants'
import AdherenceSectionCharts, {
  defaultAdherenceFilters,
  defaultAdherenceRange,
  formatAdherenceDate,
} from 'src/pages-new/analytics/AdherenceSectionCharts'
import {
  AdherenceFilterConfig,
  AdherenceFilters,
  AdherenceLabels,
  AdherenceResponse,
  AdherenceTab,
  AdherenceTherapyType,
  buildAdherenceFilterItems,
  toAdherenceFilters,
} from 'src/utils/adherenceCharts'

type PerTab<T> = Record<AdherenceTherapyType, T>

export type AdherenceFetcher = (filters: AdherenceFilters, setStatus: (status: string) => void) => Promise<AdherenceResponse>

interface AdherenceTabsProps {
  // the sections to show (Terapie / Altre terapie), already filtered by configuration and data
  tabs: AdherenceTab[]
  // one endpoint per section; must be stable (memoized) since a change reloads the section
  fetchers: PerTab<AdherenceFetcher>
  visibleFilters: AdherenceFilterConfig[]
  labels: AdherenceLabels
  // no section can be shown at all: an empty state instead of the tabs
  noSections: boolean
}

// Sections (Terapie / Altre terapie) of the adherence charts, each with its own filters. Shared by
// the patient's Analytics page and the "Aderenza terapia" tab of the doctor's patient detail, which
// only differ in the endpoints they read.
export default function AdherenceTabs({tabs, fetchers, visibleFilters, labels, noSections}: AdherenceTabsProps): JSX.Element {
  // Each tab has its own filters (and its own Filters drawer below): the drugs of the primary
  // therapies are not those of the secondary ones, so a drug picked on one tab must not carry over.
  const [filtersByTab, setFiltersByTab] = useState<PerTab<AdherenceFilters>>(() => ({
    primary: defaultAdherenceFilters(),
    secondary: defaultAdherenceFilters(),
  }))
  // options of each tab's "Farmaco" filter, from its last response
  const [drugNamesByTab, setDrugNamesByTab] = useState<PerTab<string[]>>({primary: [], secondary: []})
  // tagged with the tab it was fetched for, so switching tab never shows the other one's numbers
  const [data, setData] = useState<{therapyType: AdherenceTherapyType; response: AdherenceResponse}>()
  const [status, setStatus] = useState<string>(STATUS.PENDING)
  const [activeTab, setActiveTab] = useState<AdherenceTherapyType>('primary')

  const isActiveTabShown = tabs.some(tab => tab.type === activeTab)
  const filters = filtersByTab[activeTab]

  // One endpoint per tab: only the tab on screen is loaded.
  const fetchAdherence = useCallback(() => {
    if (!isActiveTabShown) return
    fetchers[activeTab](filters, setStatus)
      .then((response: AdherenceResponse) => {
        setData({therapyType: activeTab, response})
        setDrugNamesByTab(prev => ({...prev, [activeTab]: response?.drugNames ?? []}))
      })
      .catch(() => setData(undefined))
  }, [fetchers, filters, activeTab, isActiveTabShown])

  useEffect(() => {
    fetchAdherence()
  }, [fetchAdherence])

  useEffect(() => {
    if (!isActiveTabShown && tabs.length > 0) setActiveTab(tabs[0].type)
  }, [isActiveTabShown, tabs])

  const filterItemsByTab: PerTab<FilterItem[]> = useMemo(() => ({
    primary: buildAdherenceFilterItems(visibleFilters, defaultAdherenceRange(), drugNamesByTab.primary),
    secondary: buildAdherenceFilterItems(visibleFilters, defaultAdherenceRange(), drugNamesByTab.secondary),
  }), [visibleFilters, drugNamesByTab])

  const handleFiltersSubmit = (tab: AdherenceTherapyType, results: FilterResult[]) => {
    setFiltersByTab(prev => ({...prev, [tab]: toAdherenceFilters(results)}))
  }

  const section = data?.therapyType === activeTab ? data.response : undefined

  return (
    <Box>
      <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap'}}>
        <Typography variant="body2" color="text.secondary">
          {section ? `Periodo: ${formatAdherenceDate(section.dateFrom)} – ${formatAdherenceDate(section.dateTo)}` : ''}
        </Typography>
        {visibleFilters.length > 0 && tabs.map(({type}) => (
          // the inactive tab's drawer stays mounted, so its selection is still there when coming back
          <Box key={type} sx={{display: type === activeTab ? 'block' : 'none'}}>
            <Filters config={filterItemsByTab[type]} onSubmit={results => handleFiltersSubmit(type, results)} />
          </Box>
        ))}
      </Box>

      {noSections && (
        <EmptyState
          title="Nessuna terapia da mostrare"
          description="Non ci sono sezioni di aderenza disponibili."
        />
      )}
      {tabs.length > 0 && <Tabs
        value={isActiveTabShown ? activeTab : tabs[0].type}
        onChange={(_e, value: AdherenceTherapyType) => setActiveTab(value)}
        sx={{
          mb: 3,
          '& .MuiTab-root': {fontSize: 13, textTransform: 'none', minHeight: 40},
          '& .MuiTabs-indicator': {height: 2},
        }}
      >
        {tabs.map(tab => <Tab key={tab.type} value={tab.type} label={tab.label} />)}
      </Tabs>}

      {!noSections && status === STATUS.PENDING && (
        <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
          <CircularProgress />
        </Box>
      )}
      {isActiveTabShown && status === STATUS.REJECTED && (
        <InlineMessage severity="error">Impossibile caricare l&apos;aderenza alla terapia.</InlineMessage>
      )}
      {isActiveTabShown && status === STATUS.FULFILLED && section && (
        <AdherenceSectionCharts section={section} granularity={section.granularity} labels={labels} />
      )}
    </Box>
  )
}
